/*
  COMPONENT: Awristocrats Gallery Runtime

  Owns:
  - Gallery filtering and automatic scroll batching
  - Viewer, keyboard, pointer, and touch interactions
  - Shopify Theme Editor reinitialization
*/

(() => {
    const parser = new DOMParser();

    if (window.CurtisAwristocrats) {
      window.CurtisAwristocrats.initialize();
      return;
    }

    function cardKey(card) {
      return card.dataset.awristocratsEntry || [
        card.dataset.awristocratsViewerTitle,
        card.dataset.category,
        card.querySelector('img')?.src,
      ].filter(Boolean).join('|');
    }

    function uniqueCards(cards) {
      const seen = new Set();
      return cards.filter((card) => {
        const key = cardKey(card);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    function disableGlobalFilters(section, archiveIncomplete = false) {
      section.dataset.awristocratsGlobalFilters = 'disabled';
      const filters = section.querySelector('[data-awristocrats-filters]');
      const filterRail = filters?.closest('.awristocrats__filter-rail');
      const filterStatus = section.querySelector('[data-awristocrats-filter-status]');
      if (filters) filters.hidden = true;
      if (filterRail) filterRail.hidden = true;
      if (filterStatus) filterStatus.hidden = true;
      section.querySelector('.awristocrats__archive-index')?.setAttribute('hidden', '');
      section.querySelector('.awristocrats__spotlight')?.remove();
      const archiveError = section.querySelector('[data-awristocrats-archive-error]');
      if (archiveError && archiveIncomplete) archiveError.hidden = false;
    }

    function mergeSpotlights(section, pageSections) {
      const limit = Math.max(Number(section.dataset.awristocratsSpotlightLimit) || 0, 0);
      if (!limit) return;

      const spotlightCards = uniqueCards(
        pageSections.flatMap((pageSection) => Array.from(pageSection.querySelectorAll('.awristocrats__spotlight-card')))
      )
        .sort((a, b) => Number(a.dataset.sortOrder || 999999) - Number(b.dataset.sortOrder || 999999))
        .slice(0, limit);

      if (!spotlightCards.length) {
        section.querySelector('.awristocrats__spotlight')?.remove();
        return;
      }

      let spotlight = section.querySelector('.awristocrats__spotlight');
      if (!spotlight) {
        const source = pageSections.map((pageSection) => pageSection.querySelector('.awristocrats__spotlight')).find(Boolean);
        if (!source) return;
        spotlight = source.cloneNode(true);
        spotlight.querySelector('.awristocrats__spotlight-grid')?.replaceChildren();
        section.querySelector('[data-awristocrats-viewer]')?.before(spotlight);
      }

      spotlight.querySelector('.awristocrats__spotlight-grid')?.replaceChildren(...spotlightCards);
    }

    async function fetchArchivePage(section, page, pageParam) {
      const url = new URL(window.location.href);
      url.searchParams.set(pageParam, String(page));
      url.searchParams.set('section_id', section.dataset.awristocratsSectionId);

      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Awristocrats page request failed: ${response.status}`);

      const doc = parser.parseFromString(await response.text(), 'text/html');
      const pageSection = doc.querySelector('[data-awristocrats]');
      if (!pageSection) throw new Error('Awristocrats page response did not include the section.');
      return pageSection;
    }

    async function hydrateArchive(section) {
      const initialCards = Array.from(section.querySelectorAll('[data-awristocrats-card]'));
      const paginationData = section.querySelector('[data-awristocrats-pagination]');
      const totalPages = Number(paginationData?.dataset.totalPages || 1);
      const currentPage = Number(paginationData?.dataset.currentPage || 1);
      const pageParam = paginationData?.dataset.pageParam || 'page';

      if (totalPages <= 1) return initialCards;
      if (!section.dataset.awristocratsSectionId) {
        disableGlobalFilters(section, true);
        return initialCards;
      }

      const pages = [];
      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) continue;
        pages.push(page);
      }

      let nextPageIndex = 0;
      const fetchNextPage = async () => {
        const results = [];
        while (nextPageIndex < pages.length) {
          const page = pages[nextPageIndex++];
          results.push({ page, pageSection: await fetchArchivePage(section, page, pageParam) });
        }
        return results;
      };
      const results = await Promise.allSettled(
        Array.from({ length: Math.min(4, pages.length) }, fetchNextPage)
      );
      if (!section.isConnected) return [];
      if (results.some((result) => result.status === 'rejected')) {
        disableGlobalFilters(section, true);
        return initialCards;
      }

      const pageSections = [{ page: currentPage, pageSection: section }, ...results.flatMap((result) => result.value)]
        .sort((a, b) => a.page - b.page)
        .map((result) => result.pageSection);

      const cards = uniqueCards(
        pageSections.flatMap((pageSection) => Array.from(pageSection.querySelectorAll('[data-awristocrats-card]')))
      );

      mergeSpotlights(section, pageSections);
      return cards;
    }

    async function initializeAwristocratsSection(section) {
        const cards = await hydrateArchive(section);
        if (!section.isConnected) return;

        if (window.CurtisMotion) window.CurtisMotion.initialize(section);

        const filters = section.querySelector('[data-awristocrats-filters]');
        const filterRail = filters?.closest('.awristocrats__filter-rail');
        const filterStatus = section.querySelector('[data-awristocrats-filter-status]');
        const spotlightCards = Array.from(section.querySelectorAll('.awristocrats__spotlight-card'));
        const viewer = section.querySelector('[data-awristocrats-viewer]');
        const grid = section.querySelector('[data-awristocrats-grid]');
        const loadSentinel = section.querySelector('[data-awristocrats-load-sentinel]');
        const batchSize = Math.max(Number(section.dataset.awristocratsBatchSize) || cards.length, 1);
        const allLabel = section.dataset.awristocratsAllLabel || '';
        const filterStatusTemplate = section.dataset.awristocratsFilterStatus || '__label__: __visible__ / __total__';
        let activeFilter = null;
        let visibleLimit = batchSize;

        if (!cards.length) {
          disableGlobalFilters(section);
          return;
        }

        cards.forEach((card, index) => {
          card.dataset.originalIndex = String(index);
        });
        cards.sort((a, b) => {
          const orderA = Number(a.dataset.sortOrder || 999999);
          const orderB = Number(b.dataset.sortOrder || 999999);
          if (orderA !== orderB) return orderA - orderB;
          return Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0);
        });

        if (viewer) {
          const viewerImage = viewer.querySelector('[data-awristocrats-viewer-image]');
          const viewerTitle = viewer.querySelector('[data-awristocrats-viewer-title]');
          const viewerMeta = viewer.querySelector('[data-awristocrats-viewer-meta]');
          const viewerCaption = viewer.querySelector('[data-awristocrats-viewer-caption]');
          const viewerCount = viewer.querySelector('[data-awristocrats-viewer-count]');
          const closeButtons = viewer.querySelectorAll('[data-awristocrats-viewer-close]');
          const previousButton = viewer.querySelector('[data-awristocrats-viewer-previous]');
          const nextButton = viewer.querySelector('[data-awristocrats-viewer-next]');
          const viewerFrame = viewer.querySelector('.awristocrats__viewer-frame');
          let activeViewerItem = null;
          let activeViewerTrigger = null;
          let viewerFilter = null;
          const viewerTouch = {
            scale: 1,
            panX: 0,
            panY: 0,
            dragStartX: 0,
            dragStartY: 0,
            startPanX: 0,
            startPanY: 0,
            dragging: false,
            pinchDistance: 0,
            pinchScale: 1,
            swipeStartX: 0,
            swipeStartY: 0,
            dialogTouchStartX: 0,
            dialogTouchStartY: 0,
            lastSwipeAt: 0,
            suppressClick: false,
            pointers: new Map(),
          };

          const visibleViewerItems = () => cards.filter((item) =>
            (viewerFilter === null || item.dataset.category === viewerFilter) && item.querySelector('img')
          );
          const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);
          const pointerDistance = (points) => {
            if (points.length < 2) return 0;
            return Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY);
          };

          const applyViewerZoom = () => {
            if (!viewerFrame) return;
            if (viewerTouch.scale <= 1) {
              viewerTouch.panX = 0;
              viewerTouch.panY = 0;
            }
            viewerFrame.style.setProperty('--awristocrats-viewer-scale', viewerTouch.scale.toFixed(2));
            viewerFrame.style.setProperty('--awristocrats-viewer-pan-x', `${viewerTouch.panX}px`);
            viewerFrame.style.setProperty('--awristocrats-viewer-pan-y', `${viewerTouch.panY}px`);
            viewerFrame.classList.toggle('is-zoomed', viewerTouch.scale > 1);
          };

          const setViewerZoom = (scale) => {
            viewerTouch.scale = clampValue(scale, 1, 3);
            const maxPan = 360 * (viewerTouch.scale - 1);
            viewerTouch.panX = clampValue(viewerTouch.panX, -maxPan, maxPan);
            viewerTouch.panY = clampValue(viewerTouch.panY, -maxPan, maxPan);
            applyViewerZoom();
          };

          const resetViewerZoom = () => {
            viewerTouch.scale = 1;
            viewerTouch.panX = 0;
            viewerTouch.panY = 0;
            viewerTouch.dragging = false;
            viewerTouch.pinchDistance = 0;
            viewerTouch.pinchScale = 1;
            viewerTouch.pointers.clear();
            viewerFrame?.classList.remove('is-dragging');
            applyViewerZoom();
          };

          const setViewerItem = (item) => {
            const image = item.querySelector('img');
            if (!image || !viewerImage) return;
            const visibleItems = visibleViewerItems();
            const index = Math.max(visibleItems.indexOf(item), 0);
            activeViewerItem = item;
            viewerImage.src = image.currentSrc || image.src;
            viewerImage.alt = image.alt || item.dataset.awristocratsViewerTitle || '';
            if (viewerTitle) viewerTitle.textContent = item.dataset.awristocratsViewerTitle || '';
            if (viewerMeta) viewerMeta.textContent = [item.dataset.awristocratsViewerRole, item.dataset.awristocratsViewerCategory].filter(Boolean).join(' / ');
            if (viewerCaption) viewerCaption.textContent = item.dataset.awristocratsViewerCaption || '';
            if (viewerCount) viewerCount.textContent = `${index + 1} / ${visibleItems.length}`;
            resetViewerZoom();
          };

          const closeViewer = () => {
            viewer.hidden = true;
            section.classList.remove('awristocrats--viewer-open');
            document.documentElement.classList.remove('awristocrats-viewer-is-open');
            resetViewerZoom();
            if (activeViewerTrigger?.isConnected) activeViewerTrigger.focus();
          };

          const openViewer = (item) => {
            const galleryItem = cards.includes(item) ? item : cards.find((card) => cardKey(card) === cardKey(item));
            if (!galleryItem?.querySelector('img')) return;
            activeViewerTrigger = item;
            viewerFilter = item.matches('.awristocrats__spotlight-card') ? null : activeFilter;
            setViewerItem(galleryItem);
            viewer.hidden = false;
            section.classList.add('awristocrats--viewer-open');
            document.documentElement.classList.add('awristocrats-viewer-is-open');
            viewer.querySelector('[data-awristocrats-viewer-close]')?.focus();
          };

          const shiftViewer = (direction) => {
            const visibleItems = visibleViewerItems();
            if (!visibleItems.length || !activeViewerItem) return;
            const currentIndex = visibleItems.indexOf(activeViewerItem);
            const nextIndex = (currentIndex + direction + visibleItems.length) % visibleItems.length;
            setViewerItem(visibleItems[nextIndex]);
          };

          const trapViewerFocus = (event) => {
            if (event.key !== 'Tab' || viewer.hidden) return;
            const focusable = Array.from(viewer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
              .filter((element) => !element.disabled && element.offsetParent !== null);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          };

          [...spotlightCards, ...cards].forEach((item) => {
            item.addEventListener('click', () => openViewer(item));
            item.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              openViewer(item);
            });
          });

          closeButtons.forEach((button) => button.addEventListener('click', closeViewer));
          previousButton?.addEventListener('click', () => shiftViewer(-1));
          nextButton?.addEventListener('click', () => shiftViewer(1));
          const handleViewerSwipe = (deltaX, deltaY, pointerType = 'touch') => {
            if (pointerType === 'mouse' || viewerTouch.scale > 1) return false;
            if (Math.abs(deltaX) <= 42 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.12) return false;
            shiftViewer(deltaX > 0 ? -1 : 1);
            viewerTouch.lastSwipeAt = Date.now();
            viewerTouch.suppressClick = true;
            return true;
          };
          viewerFrame?.addEventListener('click', () => {
            if (viewerTouch.suppressClick) {
              viewerTouch.suppressClick = false;
              return;
            }
            setViewerZoom(viewerTouch.scale > 1 ? 1 : 2);
          });
          viewer.querySelector('.awristocrats__viewer-dialog')?.addEventListener('touchstart', (event) => {
            if (event.target.closest('button') || event.touches.length !== 1) return;
            viewerTouch.dialogTouchStartX = event.touches[0].clientX;
            viewerTouch.dialogTouchStartY = event.touches[0].clientY;
          }, { passive: true });
          viewer.querySelector('.awristocrats__viewer-dialog')?.addEventListener('touchend', (event) => {
            if (event.target.closest('button') || !event.changedTouches.length) return;
            if (Date.now() - viewerTouch.lastSwipeAt < 320) return;
            const touch = event.changedTouches[0];
            const handled = handleViewerSwipe(touch.clientX - viewerTouch.dialogTouchStartX, touch.clientY - viewerTouch.dialogTouchStartY);
            if (handled) event.preventDefault();
          }, { passive: false });
          viewerFrame?.addEventListener('pointerdown', (event) => {
            viewerTouch.pointers.set(event.pointerId, event);
            viewerFrame.setPointerCapture?.(event.pointerId);
            viewerTouch.swipeStartX = event.clientX;
            viewerTouch.swipeStartY = event.clientY;

            if (viewerTouch.pointers.size === 2) {
              const points = Array.from(viewerTouch.pointers.values());
              viewerTouch.pinchDistance = pointerDistance(points);
              viewerTouch.pinchScale = viewerTouch.scale;
              return;
            }

            if (viewerTouch.scale <= 1) return;
            viewerTouch.dragging = true;
            viewerTouch.dragStartX = event.clientX;
            viewerTouch.dragStartY = event.clientY;
            viewerTouch.startPanX = viewerTouch.panX;
            viewerTouch.startPanY = viewerTouch.panY;
            viewerFrame.classList.add('is-dragging');
          });
          viewerFrame?.addEventListener('pointermove', (event) => {
            if (viewerTouch.pointers.has(event.pointerId)) viewerTouch.pointers.set(event.pointerId, event);

            if (viewerTouch.pointers.size === 2 && viewerTouch.pinchDistance > 0) {
              const points = Array.from(viewerTouch.pointers.values());
              const distance = pointerDistance(points);
              setViewerZoom(viewerTouch.pinchScale * (distance / viewerTouch.pinchDistance));
              viewerTouch.suppressClick = true;
              return;
            }

            if (!viewerTouch.dragging) return;
            viewerTouch.panX = viewerTouch.startPanX + event.clientX - viewerTouch.dragStartX;
            viewerTouch.panY = viewerTouch.startPanY + event.clientY - viewerTouch.dragStartY;
            setViewerZoom(viewerTouch.scale);
            viewerTouch.suppressClick = true;
          });
          ['pointerup', 'pointercancel'].forEach((eventName) => {
            viewerFrame?.addEventListener(eventName, (event) => {
              const deltaX = event.clientX - viewerTouch.swipeStartX;
              const deltaY = event.clientY - viewerTouch.swipeStartY;
              viewerTouch.pointers.delete(event.pointerId);
              if (viewerTouch.pointers.size < 2) {
                viewerTouch.pinchDistance = 0;
                viewerTouch.pinchScale = viewerTouch.scale;
              }
              viewerTouch.dragging = false;
              viewerFrame.classList.remove('is-dragging');
              if (viewerFrame.hasPointerCapture?.(event.pointerId)) {
                viewerFrame.releasePointerCapture(event.pointerId);
              }
              if (handleViewerSwipe(deltaX, deltaY, event.pointerType)) {
                event.preventDefault();
              }
            });
          });
          viewer.addEventListener('keydown', (event) => {
            trapViewerFocus(event);
            if (event.key === 'Escape') closeViewer();
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              shiftViewer(event.key === 'ArrowLeft' ? -1 : 1);
            }
          });
        }

        const matchingCards = () => cards.filter((card) => activeFilter === null || card.dataset.category === activeFilter);
        const filterLabel = () => {
          const activeButton = Array.from(filters?.querySelectorAll('[data-awristocrats-filter]') || [])
            .find((button) => button.dataset.awristocratsCategory === activeFilter);
          return activeButton?.dataset.awristocratsFilterLabel || activeButton?.textContent?.trim() || activeFilter;
        };

        const setButtonContent = (button, label, count) => {
          button.dataset.awristocratsFilterLabel = label;
          button.textContent = '';

          const labelSpan = document.createElement('span');
          labelSpan.textContent = label;
          button.append(labelSpan);

          const countSpan = document.createElement('span');
          countSpan.className = 'awristocrats__filter-count';
          countSpan.textContent = count;
          button.append(countSpan);
        };

        const applyCardVisibility = () => {
          const matching = matchingCards();
          const visibleCards = matching.slice(0, visibleLimit);
          grid?.replaceChildren(...visibleCards);
          if (loadSentinel) loadSentinel.hidden = visibleLimit >= matching.length;

          if (filterStatus) {
            const visibleCount = Math.min(visibleLimit, matching.length);
            const label = activeFilter === null ? allLabel : filterLabel();
            filterStatus.textContent = filterStatusTemplate
              .replace('__label__', label)
              .replace('__visible__', String(visibleCount))
              .replace('__total__', String(matching.length));
          }
        };

        const loadCardsInView = () => {
          if (!loadSentinel || !loadSentinel.isConnected) return;
          while (visibleLimit < matchingCards().length &&
            loadSentinel.getBoundingClientRect().top <= window.innerHeight + 600) {
            visibleLimit += batchSize;
            applyCardVisibility();
          }
        };

        const scrollToFilterOrigin = () => {
          const target = section.querySelector('[data-awristocrats-filter-anchor]') || filterRail || section.querySelector('[data-awristocrats-gallery]');
          if (!target) return;

          const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--site-header-height')) || 0;
          const scroller = document.scrollingElement || document.documentElement;

          window.requestAnimationFrame(() => {
            const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerOffset - 12;
            scroller.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
          });
        };

        if (filters && section.dataset.awristocratsGlobalFilters !== 'disabled') {
          const categories = new Map();
          cards.forEach((card) => {
            const category = card.dataset.category;
            const label = card.dataset.categoryLabel;
            if (!category || !label) return;
            if (!categories.has(category)) categories.set(category, { label, count: 0 });
            categories.get(category).count += 1;
          });

          const allButton = filters.querySelector('[data-awristocrats-filter="all"]');
          if (allButton) {
            setButtonContent(allButton, allButton.textContent.trim() || allLabel, cards.length);
          }

          categories.forEach(({ label, count }, category) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.awristocratsFilter = 'category';
            button.dataset.awristocratsCategory = category;
            button.setAttribute('aria-pressed', 'false');
            setButtonContent(button, label, count);
            filters.append(button);
          });

          filters.hidden = categories.size === 0;
          if (filterRail) filterRail.hidden = categories.size === 0;
          if (filterStatus) filterStatus.hidden = categories.size === 0;

          filters.addEventListener('click', (event) => {
            const button = event.target.closest('[data-awristocrats-filter]');
            if (!button) return;
            activeFilter = button.dataset.awristocratsFilter === 'all' ? null : button.dataset.awristocratsCategory;
            visibleLimit = batchSize;

            filters.querySelectorAll('[data-awristocrats-filter]').forEach((filterButton) => {
              const active = filterButton === button;
              filterButton.classList.toggle('is-active', active);
              filterButton.setAttribute('aria-pressed', active ? 'true' : 'false');
            });

            applyCardVisibility();
            scrollToFilterOrigin();
            window.requestAnimationFrame(loadCardsInView);
          });
        }

        applyCardVisibility();
        if (loadSentinel) {
          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
              if (!section.isConnected) {
                observer.disconnect();
                return;
              }
              if (entries.some((entry) => entry.isIntersecting)) loadCardsInView();
            }, { rootMargin: '600px 0px' });
            observer.observe(loadSentinel);
            section.awristocratsLoadObserver = observer;
          } else {
            window.addEventListener('scroll', loadCardsInView, { passive: true });
            window.addEventListener('resize', loadCardsInView);
            section.awristocratsLoadCleanup = () => {
              window.removeEventListener('scroll', loadCardsInView);
              window.removeEventListener('resize', loadCardsInView);
            };
          }
          loadCardsInView();
        }
    }

    function initializeAwristocrats(root = document) {
      root.querySelectorAll('[data-awristocrats]').forEach((section) => {
        if (section.dataset.awristocratsInitialized === 'true') return;
        section.dataset.awristocratsInitialized = 'true';
        initializeAwristocratsSection(section).catch(() => {
          section.dataset.awristocratsInitialized = 'false';
        });
      });
    }

    window.CurtisAwristocrats = {
      initialize: initializeAwristocrats,
    };

    initializeAwristocrats();
    document.addEventListener('shopify:section:load', (event) => initializeAwristocrats(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[data-awristocrats]').forEach((section) => {
        section.awristocratsLoadObserver?.disconnect();
        section.awristocratsLoadCleanup?.();
      });
    });

})();
  